/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2024 (original work) Open Assessment Technologies SA ;
 */

import { isEmpty } from '../../helpers/objectHelper.js';

const RESULT_ITEMS_CREATORS = [
    ({ results, isRunningAudioTest, translate, configuration, components }) => ({
        title: translate('pages.diagnostic.Results.bandwidth'),
        isVisible: true,
        key: isRunningAudioTest ? 'audioCompatibility' : 'connectionCompatibility',
        get items() {
            const bandwidthResult = results[this.key];
            return [
                {
                    title: translate('pages.diagnostic.Results.download'),
                    component: components.BandwidthCheckResultItem,
                    value: {
                        ...toHumanReadable(bandwidthResult.downloadSpeed, 'speed'),
                        isSupported:
                            configuration[this.key].download.acceptable <=
                            bandwidthResult.downloadSpeed
                    }
                },
                {
                    title: translate('pages.diagnostic.Results.upload'),
                    component: components.BandwidthCheckResultItem,
                    value: {
                        ...toHumanReadable(bandwidthResult.uploadSpeed, 'speed'),
                        isSupported:
                            configuration[this.key].upload.acceptable <=
                            bandwidthResult.uploadSpeed
                    }
                },
                {
                    title: translate('pages.diagnostic.Results.latency'),
                    component: components.BandwidthCheckResultItem,
                    value: {
                        ...toHumanReadable(bandwidthResult.latency, 'time'),
                        isSupported:
                            configuration[this.key].latency.acceptable >=
                            bandwidthResult.latency
                    }
                }
            ];
        },
        get info() {
            return translate(
                'pages.diagnostic.Results.bandwidth_result_based',
                { d: configuration[this.key].concurrentUsers }
            );
        },
        get status() {
            return this.items.every(item => item.value.isSupported) ? 'success' : 'warning';
        }
    }),
    ({ results, translate, components }) => ({
        isVisible: true,
        title: translate('pages.diagnostic.Results.browser'),
        key: 'browserCompatibility',
        get items() {
            return [
                {
                    title: translate('pages.diagnostic.Results.browser'),
                    component: components.BrowserCheckResultItem,
                    value: {
                        name: results[this.key].browser.name,
                        isSupported: results[this.key].result.listed
                    }
                },
                {
                    title: translate('pages.diagnostic.Results.version'),
                    component: components.BrowserCheckResultItem,
                    value: {
                        name: results[this.key].browser.version,
                        isSupported: results[this.key].result.accepted
                    }
                }
            ];
        },
        get status() {
            return this.items.every(item => item.value.isSupported) ? 'success' : 'warning';
        }
    }),
    ({ results, translate, components }) => ({
        isVisible: true,
        key: 'endpointsReachability',
        title: translate('pages.diagnostic.Results.connectivity'),
        listStyle: 'vertical',
        get items() {
            return Object.keys(results.endpointsReachability).map((resultKey) => ({
                title: resultKey,
                component: components.ConnectivityCheckResultItem,
                value: results[this.key][resultKey],
            }));
        },
        get status() {
            return this.items.every(item => item.value.reachable) ? 'success' : 'warning';
        }
    }),
    ({ results, translate }) => ({
        isVisible: true,
        title: translate('pages.diagnostic.Results.webSocketConnection'),
        key: 'webSocketCompatibility',
        get info() {
            return results[this.key].enabled
                ? translate('components.WebSocketCheckResultItem.yes')
                : translate('components.WebSocketCheckResultItem.no');
        },
        get status() {
            return results[this.key].enabled ? 'success' : 'warning';
        },
    }),
    ({ translate, results, isRunningAudioTest, components }) => ({
        isVisible: isRunningAudioTest,
        title: translate('pages.diagnostic.Results.audio'),
        key: 'audio',
        get items() {
            return [
                {
                    title: translate('pages.diagnostic.Results.playback'),
                    component: components.AudioCheckResultItem,
                    value: {
                        isSupported: results[this.key],
                    }
                }
            ];
        },
        get status() {
            return this.items.every(item => item.value.isSupported) ? 'success' : 'warning';
        }
    })
];

/**
 * Converts value to human-readable form
 * @param {number} value
 * @param {string} type
 * @returns {object}
 */
function toHumanReadable(value, type) {
    switch (type) {
        case 'speed':
            return humanReadableSpeed(value);
        case 'time':
            return humanReadableTime(value);
        default:
            return { value };
    }
}

function humanReadableSpeed(speed) {
    if (speed === null) {
        return {
            value: 'N/A',
            unit: ''
        };
    }
    const units = ['bytes/s', 'KB/s', 'MB/s'];
    let i = 0;
    while (speed > 1024) {
        speed /= 1024;
        i++;
    }
    return {
        value: speed.toFixed(2),
        unit: units[i]
    };
}

function humanReadableTime(time) {
    const units = ['ms', 's', 'm', 'h', 'd'];
    let i = 0;
    while (time > 1000) {
        time /= 1000;
        i++;
    }
    return {
        value: time.toFixed(2),
        unit: units[i]
    };
}

/**
 * @param {Object} object
 * @returns {Object}
 */
export function omitEmpty(object) {
    const newObject = {};
    for (const key in object) {
        const value = object[key];
        if (value != null) { // skip null & undefined
            newObject[key] = value;
        }
    }
    return newObject;
}

/**
 * @param {Object} results
 * @param {{key: string}} item
 * @returns {boolean}
 */
export const hasResults = (results, item) => {
    const result = results[item.key];
    return result === false || // Allow false as result value
        (result != null && !isEmpty(result));
}

/**
 * @typedef {Object} ResultItem
 * @property {boolean} isVisible
 * @property {string} title
 * @property {string} key
 * @property {boolean} readonly
 * @property {{component: SvelteComponent, title: string, value: {isSupported: boolean, value: (number|string|*)}}[]} items
 *
 * @param {{}} args
 * @param {{}} args.results
 * @param {{}} args.configuration
 * @param {{}} args.components
 * @param {boolean} args.isRunningAudioTest
 * @param {Function} args.translate
 * @returns {ResultItem[]}
 */
export function getDiagnosticResultsItems(args) {
    const { results } = args;
    return RESULT_ITEMS_CREATORS
        .map(createItem => {
            const item = createItem(args);
            const isItemVisible = hasResults(results, item) && item.isVisible;
            return isItemVisible ? item : null;
        })
        .filter(item => !!item);
}